import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  getMarketingConsent,
  setMarketingConsent,
  CONSENT_EVENT,
} from "@/lib/consent";
import { initPixels } from "@/lib/pixels";

// Bottom banner asking for marketing-cookie consent.
// Hidden once a choice exists; can be reopened via the footer
// "Cookie preferences" button (which dispatches OPEN_EVENT).
export const OPEN_CONSENT_EVENT = "phl:open-consent";

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only when no choice has been recorded yet.
    setVisible(getMarketingConsent() === null);

    const open = () => setVisible(true);
    const changed = () => setVisible(getMarketingConsent() === null);

    window.addEventListener(OPEN_CONSENT_EVENT, open);
    window.addEventListener(CONSENT_EVENT, changed);
    return () => {
      window.removeEventListener(OPEN_CONSENT_EVENT, open);
      window.removeEventListener(CONSENT_EVENT, changed);
    };
  }, []);

  const choose = (choice: "accept" | "essential") => {
    setMarketingConsent(choice);
    setVisible(false);
    if (choice === "accept") initPixels();
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed bottom-0 inset-x-0 z-[90] border-t border-border bg-card/95 supports-[backdrop-filter]:bg-card/80 supports-[backdrop-filter]:backdrop-blur"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <p className="text-sm leading-relaxed text-muted-foreground flex-1">
          Essential cookies run cart and checkout. With permission, marketing cookies measure ads. See our{" "}
          <Link to="/privacy" className="underline text-foreground">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="grid grid-cols-2 gap-2 shrink-0 sm:flex">
          <Button
            variant="outline"
            size="sm"
            onClick={() => choose("essential")}
            className="min-h-11 touch-manipulation"
          >
            Essential only
          </Button>
          <Button
            size="sm"
            onClick={() => choose("accept")}
            className="min-h-11 touch-manipulation"
          >
            Accept marketing
          </Button>
        </div>
      </div>
    </div>
  );
}
